import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Box, Typography, Tabs, Tab, Avatar, ThemeProvider, Button, Autocomplete, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "system/store";
import { muiTheme } from "system/styles/muiTheme";
import useAppNavigation from "system/hooks/use-app-navigation";
import { useApi, useConfiguration, usePolicyEditorPermissions } from "system/hooks";
import { ListResponse } from "system/types/wireTypes";
import ApplicabilityRuleTab from "./applicabilityTab/ApplicabilityRuleTab";
import { policyEditorActions } from "system/store/slices/policy-editor-slice";
import { ConfigurationTab } from "./configurationTab/ConfigurationTab";
import { InheritedConfigurationTab } from "./inheritedConfigurationTab/InheritedConfigurationTab";
import { isPartialPatternMatch } from "../../utils/stringExtensions";
import { emulateTab } from "emulate-tab";
import _ from "lodash";
import { IniInfo, UnconfiguredRules } from "system/types/interfaces";
import { ErrorMessage } from "components/ui";
import { findLastIndexManual } from "system/libraries/utilities";
import { survivesPackageSwitch, withDefaultConfigurations } from "../../constants/defaultConfigurationsForNotConfigured";
import { haveConfigurationsChanged, isConfigurationLineChanged } from "../../utils/configurationChanges";

const PolicyEditorSubjectView = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const { currentTenant } = useConfiguration();
  const { isReadOnly: readOnlyViewer } = usePolicyEditorPermissions();
  const selectedPackage = useSelector((state: RootState) => state.policyEditor.selectedPackage);
  const rules = useSelector((state: RootState) => state.policyEditor.rules);
  const modifiedRows = useSelector((state: RootState) => state.policyEditor.modifiedRows);
  const packages = useSelector((state: RootState) => state.policyEditor.packages);
  const globalCatalog = useSelector((state: RootState) => state.policyEditor.globalCatalog);
  const pendingReplicateChanges = useSelector((state: RootState) => state.policyEditor.pendingReplicateChanges);
  const configurationByPackage = useSelector((state: RootState) => state.policyEditor.configurationByPackage);
  const inheritedConfigurations = useSelector((state: RootState) => state.policyEditor.inheritedConfigurations);
  const currentVersionId = useSelector((state: RootState) => state.policyEditor.currentVersionId);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  const [localConfigurations, setLocalConfigurations] = useState<IniInfo[]>([]);
  const [initialSubjectName, setInitialSubjectName] = useState<string | null>(null);
  const [initialActivePackageName, setInitialActivePackageName] = useState<string | null>(null);
  const [notConfiguredRules, setNotConfiguredRules] = useState<UnconfiguredRules[]>([]);
  const initialConfigurationsRef = useRef<IniInfo[] | null>(null);
  const loadedConfigsForRef = useRef<string | null>(null);

  const dispatch = useDispatch();
  const navigate = useAppNavigation();
  const api = useApi();

  const [isPackageDropdownOpen, setIsPackageDropdownOpen] = useState(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const isFirstInput = useRef(true);
  const initialAutoSelectDoneRef = useRef(false);

  const selectedIcon = useMemo(() => {
    if (!selectedPackage?.activePackageName) {
      return "";
    }
    const matchingPackage = packages.find((pkg) => pkg.value === selectedPackage.activePackageName)
      ?? globalCatalog.find((pkg) => pkg.value === selectedPackage.activePackageName);
    return matchingPackage?.iconUrl || "";
  }, [selectedPackage?.activePackageName, packages, globalCatalog]);

  type PackageOption = { value: string; source: "installed" | "catalog" | "pending" | "removing" };

  const dropdownOptions = useMemo<PackageOption[]>(() => {
    const subjectName = (selectedPackage?.rule?.name || "").trim();
    const ruleTokens = subjectName.split(/\s+/).filter(Boolean);

    const removalsSet = new Set(pendingReplicateChanges.removals.map((r) => r.toLowerCase()));
    const seen = new Set<string>();
    const allOptions: PackageOption[] = [];
    pendingReplicateChanges.additions.forEach((value) => {
      if (!value) return;
      const key = value.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      allOptions.push({ value, source: "pending" });
    });
    packages.forEach((p) => {
      if (!p.value) return;
      const key = p.value.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      allOptions.push({ value: p.value, source: removalsSet.has(key) ? "removing" : "installed" });
    });
    globalCatalog.forEach((p) => {
      if (!p.value) return;
      const key = p.value.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      allOptions.push({ value: p.value, source: "catalog" });
    });

    if (ruleTokens.length === 0) {
      return allOptions.sort((a, b) => a.value.localeCompare(b.value));
    }

    let contextLen = ruleTokens.length;
    for (let i = 1; i < ruleTokens.length; i++) {
      if (/\d/.test(ruleTokens[i])) {
        contextLen = i + 1;
        break;
      }
    }

    const commonPrefixCount = (pkgValue: string): number => {
      const pkgTokens = pkgValue.split(/\s+/).filter(Boolean);
      const limit = Math.min(ruleTokens.length, pkgTokens.length);
      let n = 0;
      while (n < limit && ruleTokens[n].toLowerCase() === pkgTokens[n].toLowerCase()) {
        n++;
      }
      return n;
    };

    while (contextLen > 0) {
      const prefix = ruleTokens.slice(0, contextLen).join(" ").toLowerCase();
      const matches = allOptions.filter((opt) => {
        const lower = opt.value.toLowerCase();
        return lower === prefix || lower.startsWith(prefix + " ");
      });
      if (matches.length > 0) {
        return matches.sort((a, b) => {
          const diff = commonPrefixCount(b.value) - commonPrefixCount(a.value);
          if (diff !== 0) return diff;
          return a.value.localeCompare(b.value);
        });
      }
      contextLen--;
    }

    return [];
  }, [selectedPackage?.rule?.name, packages, globalCatalog, pendingReplicateChanges.additions, pendingReplicateChanges.removals]);

  const noApplicablePackage = !(selectedPackage?.activePackageName || "").trim();

  const subjectMissingFromLibrary = useMemo(() => {
    const ruleName = (selectedPackage?.rule?.name || "").trim();
    if (!ruleName) return false;
    const tokens = ruleName.split(/\s+/).filter(Boolean);
    const hasVersion = tokens.slice(1).some((t) => /\d/.test(t));
    if (!hasVersion) return false;
    const activeName = (selectedPackage?.activePackageName || "").trim();
    const removedSet = new Set(pendingReplicateChanges.removals.map((r) => r.toLowerCase()));
    const candidates = [
      ...packages.map((p) => p.value).filter((v): v is string => Boolean(v) && !removedSet.has(v.toLowerCase())),
      ...pendingReplicateChanges.additions,
      activeName,
    ].filter(Boolean);
    return !candidates.some((v) => isPartialPatternMatch(v, ruleName));
  }, [selectedPackage?.rule?.name, selectedPackage?.activePackageName, packages, pendingReplicateChanges.additions, pendingReplicateChanges.removals]);

  useEffect(() => {
    if (initialAutoSelectDoneRef.current) return;
    if (!selectedPackage) return;
    if (packages.length === 0 && globalCatalog.length === 0) return;

    if (selectedPackage.fromMissingModal) {
      const active = (selectedPackage.activePackageName || "").trim();
      if (!active) {
        setIsPackageDropdownOpen(true);
      }
      initialAutoSelectDoneRef.current = true;
      return;
    }

    const active = (selectedPackage.activePackageName || "").trim();
    const installedOrPending = dropdownOptions.filter((o) => o.source === "installed" || o.source === "pending");

    if (active && installedOrPending.some((o) => o.value === active)) {
      initialAutoSelectDoneRef.current = true;
      return;
    }

    if (installedOrPending.length > 0) {
      const sorted = [...installedOrPending].sort((a, b) =>
        a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: "base" })
      );
      dispatch(policyEditorActions.updateSelectedPackageActiveName(sorted[sorted.length - 1].value));
      initialAutoSelectDoneRef.current = true;
      return;
    }

    setIsPackageDropdownOpen(true);
    initialAutoSelectDoneRef.current = true;
  }, [selectedPackage?.rule?.id, packages.length, globalCatalog.length, dropdownOptions, dispatch, selectedPackage?.fromMissingModal, selectedPackage?.activePackageName]);

  useEffect(() => {
    if (!selectedPackage) {
      navigate(currentVersionId ? `/policyeditor/edit/${currentVersionId}` : "/policyeditor");
      return;
    }

    if (selectedPackage?.rule?.name) {
      setInitialSubjectName(selectedPackage.rule.name);
    }
    if (initialActivePackageName === null && selectedPackage?.activePackageName && selectedPackage.activePackageName.trim().length > 0) {
      setInitialActivePackageName(selectedPackage.activePackageName);
    }
    if (selectedPackage?.activePackageName && currentTenant) {
      api.policyEditor.getUnconfigured(currentTenant.shortName, selectedPackage.activePackageName).then((response) => {
        const defaults = response.isSuccess && response.items ? response.items : null;
        setNotConfiguredRules(withDefaultConfigurations(defaults));
        if (!defaults) return;

        const withType = (lines: IniInfo[]) =>
          lines.map((c) => {
            const defaultRule = defaults.find((r) => r.name === c.name);
            return defaultRule ? { ...c, type: defaultRule.type, valueShouldBeLogged: defaultRule.valueShouldBeLogged } : c;
          });

        // This runs again on every change to selectedPackage, and a configuration only
        // reaches the store on Save, so reloading them here threw away what the user had
        // just added. Load them once per subject and leave the working copy alone.
        const subjectKey = selectedPackage.rule.id ?? String(selectedPackage.index ?? "");
        if (loadedConfigsForRef.current === subjectKey) {
          setLocalConfigurations((prev) => withType(prev));
          return;
        }

        const loaded = withType(_.cloneDeep(configurationByPackage[selectedPackage.rule.name] || []));
        loadedConfigsForRef.current = subjectKey;
        setLocalConfigurations(loaded);
        if (initialConfigurationsRef.current === null) {
          initialConfigurationsRef.current = _.cloneDeep(loaded);
        }
      });
    }
  }, [api.policyEditor, configurationByPackage, currentTenant, navigate, selectedPackage]);

  const handlePackageNameChange = useCallback(
    async (_: any, newValue: string) => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      const updatePackageData = async () => {
        dispatch(policyEditorActions.updateSelectedPackageName(newValue || ""));

        const matchingPending = (newValue || "").length > 0
          ? pendingReplicateChanges.additions.filter((a) => isPartialPatternMatch(a, newValue))
          : [];
        const preferredPending = matchingPending.length > 0 ? matchingPending[matchingPending.length - 1] : null;

        if (preferredPending) {
          dispatch(policyEditorActions.updateSelectedPackageActiveName(preferredPending));
        } else {
          dispatch(policyEditorActions.updateSelectedPackageActiveName(""));
        }

        if (currentTenant?.shortName && newValue) {
          try {
            const data: ListResponse<string> = await api.policyEditor.getPackageMatch(currentTenant.shortName, newValue);
            if (data?.isSuccess && data.items && data.items.length > 0) {
              const newName = preferredPending ?? data.items[data.items.length - 1];
              dispatch(policyEditorActions.updateSelectedPackageActiveName(newName));
              if (data.items.length > 1) {
                emulateTab();
              }

              const response = await api.policyEditor.getUnconfigured(currentTenant.shortName, newName);
              setNotConfiguredRules(withDefaultConfigurations(response.isSuccess ? response.items : null));

              setLocalConfigurations((prev) => {
                if (!response.items || response.items.length === 0) {
                  return prev.filter((config) => config.isComment || survivesPackageSwitch(config.name));
                }

                const defaultConfigNames = response.items.map((item) => item.name);
                return prev.filter(
                  (config) =>
                    defaultConfigNames.includes(config.name) || config.isComment || survivesPackageSwitch(config.name)
                );
              });
            } else {
              setNotConfiguredRules(withDefaultConfigurations());
              setLocalConfigurations((prev) =>
                prev.filter((config) => config.isComment || survivesPackageSwitch(config.name))
              );
            }
          } catch (error) {
            console.error("Error fetching package matches:", error);
            setNotConfiguredRules(withDefaultConfigurations());
            setLocalConfigurations((prev) =>
              prev.filter((config) => config.isComment || survivesPackageSwitch(config.name))
            );
          }
        }
      };

      if (isFirstInput.current) {
        updatePackageData();
        isFirstInput.current = false;
      } else {
        debounceTimeout.current = setTimeout(updatePackageData, 700);
      }
    },
    [dispatch, api.policyEditor, currentTenant?.shortName, pendingReplicateChanges.additions]
  );

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const checkForDuplicateRule = useCallback(
    (newRule: IniInfo, currentIndex: number | null): boolean => {
      const newRuleString = `${newRule.name} ${newRule.delimiter} ${newRule.value}`.trim();
      return rules.some((existingRule: IniInfo, idx) => {
        if (existingRule.name.includes("-comment-")) return false;
        if (currentIndex !== null && idx === currentIndex) return false;
        const existingRuleString = `${existingRule.name} ${existingRule.delimiter} ${existingRule.value}`.trim();
        return existingRuleString === newRuleString;
      });
    },
    [rules]
  );

  const handleInsertConfiguredItem = useCallback((item: IniInfo, position?: number) => {
    setLocalConfigurations((prev) => {
      const copy = [...prev];
      if (item.name === "Pre-run Command") {
        const lastPreRunIndex = findLastIndexManual(copy, (line) => line.name === "Pre-run Command");
        if (lastPreRunIndex >= 0) {
          copy.splice(lastPreRunIndex + 1, 0, item);
        } else {
          copy.unshift(item);
        }
      } else if (item.name === "Post-run Command") {
        copy.push(item);
      } else {
        if (position !== undefined) {
          copy.splice(position, 0, item);
        } else {
          copy.push(item);
        }
      }
      item.pendingChanges = true;
      return copy;
    });
  }, []);

  const handleUpdateConfiguredItem = useCallback((item: IniInfo) => {
    setLocalConfigurations((prev) => {
      const copy = [...prev];
      const index = copy.findIndex((c) => c.id === item.id);
      if (index >= 0) {
        copy[index] = { ...item, pendingChanges: isConfigurationLineChanged(item) };
      }
      return copy;
    });
  }, []);

  const handleDeleteConfiguredItem = useCallback((id: string) => {
    setLocalConfigurations((prev) => {
      const copy = [...prev];
      const index = copy.findIndex((c) => c.id === id);
      if (index >= 0) {
        copy[index] = { ...copy[index], isDeleted: true };
      }
      return copy;
    });
  }, []);

  const handleUndoDeleteConfiguredItem = useCallback((id: string) => {
    setLocalConfigurations((prev) => {
      const copy = [...prev];
      const index = copy.findIndex((c) => c.id === id);
      if (index >= 0) {
        copy[index] = { ...copy[index], isDeleted: false };
      }
      return copy;
    });
  }, []);

  const handleReorderConfiguredItems = useCallback((oldIndex: number, newIndex: number) => {
    setLocalConfigurations((prev) => {
      const copy = [...prev];
      const [moved] = copy.splice(oldIndex, 1);
      moved.pendingChanges = true;
      copy.splice(newIndex, 0, moved);
      return copy;
    });
  }, []);

  const handleSave = () => {
    if (!selectedPackage?.rule) return;

    const { index, rule, isUpdating } = selectedPackage;

    if (index == null) {
      return;
    }

    const isDuplicate = checkForDuplicateRule(rule, index);
    if (isDuplicate) {
      setSaveErrorMessage("The rule you are trying to add already exists in the list of rules.");
      return;
    }

    const currentSubjectName = rule.name;

    if (initialSubjectName && initialSubjectName !== currentSubjectName) {
      dispatch(
        policyEditorActions.setConfigurationForPackage([
          {
            name: initialSubjectName,
            lines: [],
          },
        ])
      );
    }

    if (localConfigurations.length > 0) {
      dispatch(
        policyEditorActions.setConfigurationForPackage([
          {
            name: currentSubjectName,
            lines: localConfigurations,
          },
        ])
      );
    } else {
      dispatch(
        policyEditorActions.setConfigurationForPackage([
          {
            name: currentSubjectName,
            lines: [],
          },
        ])
      );
    }

    const configChanged = haveConfigurationsChanged(localConfigurations, initialConfigurationsRef.current ?? []);

    if (!isUpdating) {
      dispatch(policyEditorActions.addRule({ index, rule }));
    } else {
      dispatch(policyEditorActions.updateRule({ index, updatedRule: rule }));
    }

    if (configChanged) {
      dispatch(policyEditorActions.updateModifiedRows(rule.id!));
    }

    const newActivePackageName = (selectedPackage.activePackageName || currentSubjectName || "").trim();
    const oldActivePackageName = (initialActivePackageName || "").trim();

    if (oldActivePackageName && oldActivePackageName !== newActivePackageName) {
      const oldInReplicate = packages.some((p) => p.value && p.value.toLowerCase() === oldActivePackageName.toLowerCase());
      const oldInAdditions = pendingReplicateChanges.additions.some((a) => a.toLowerCase() === oldActivePackageName.toLowerCase());

      if (oldInAdditions) {
        dispatch(policyEditorActions.removePendingReplicateAddition(oldActivePackageName));
      } else if (oldInReplicate) {
        const otherRuleUsesIt = rules.some((r, i) => {
          if (i === index) return false;
          if (r.isDeleted || r.isComment || !r.name) return false;
          return isPartialPatternMatch(oldActivePackageName, r.name.trim()) || r.name.trim().toLowerCase() === oldActivePackageName.toLowerCase();
        });
        if (!otherRuleUsesIt) {
          dispatch(policyEditorActions.addPendingReplicateRemoval(oldActivePackageName));
        }
      }
    }

    if (newActivePackageName) {
      const inReplicate = packages.some((p) => p.value && p.value.toLowerCase() === newActivePackageName.toLowerCase());
      const inPending = pendingReplicateChanges.additions.some((a) => a.toLowerCase() === newActivePackageName.toLowerCase());
      const inRemovals = pendingReplicateChanges.removals.some((r) => r.toLowerCase() === newActivePackageName.toLowerCase());
      if (inRemovals) {
        dispatch(policyEditorActions.removePendingReplicateRemoval(newActivePackageName));
      } else if (!inReplicate && !inPending) {
        dispatch(policyEditorActions.addPendingReplicateAddition(newActivePackageName));
      }
    }

    dispatch(policyEditorActions.removeInvalidRule(index + 1));

    dispatch(policyEditorActions.setLastEditedIndex(index));
    navigate(currentVersionId ? `/policyeditor/edit/${currentVersionId}` : "/policyeditor");
  };

  const handleNext = () => setCurrentTab((prev) => Math.min(prev + 1, 1));
  const handlePrevious = () => setCurrentTab((prev) => Math.max(prev - 1, 0));
  const handleCancel = () => {
    if (!selectedPackage?.rule) return;

    const { index } = selectedPackage;

    if (index == null) {
      return;
    }

    dispatch(
      policyEditorActions.setConfigurationForPackage([
        {
          name: selectedPackage.rule.name,
          lines: initialConfigurationsRef.current ?? [],
        },
      ])
    );

    dispatch(policyEditorActions.setLastEditedIndex(index));
    navigate(currentVersionId ? `/policyeditor/edit/${currentVersionId}` : "/policyeditor");
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (modifiedRows.length > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    const handleNavigation = (e: PopStateEvent) => {
      if (modifiedRows.length > 0 && window.location.pathname === "/policyeditor") {
        e.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handleNavigation);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handleNavigation);
    };
  }, [modifiedRows.length]);

  const inheritedConfigurationsTabs = useMemo(() => {
    if (!selectedPackage?.activePackageName || selectedPackage.activePackageName === undefined || !inheritedConfigurations) return [];
    const filtered = inheritedConfigurations.filter((c) => c.matchingPackages.includes(selectedPackage.activePackageName ?? ""));
    return filtered;
  }, [inheritedConfigurations, selectedPackage?.activePackageName]);

  useEffect(() => {
    if (inheritedConfigurationsTabs.length === 0 && currentTab >= 2) {
      setCurrentTab(0);
    }
  }, [inheritedConfigurationsTabs, currentTab]);

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "text.primary" }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={2}>
          {selectedPackage?.icon || selectedIcon ? (
            <Avatar
              src={selectedPackage?.icon || selectedIcon}
              alt="Package Icon"
              sx={{
                width: 48,
                height: 48,
                mr: 2,
                borderRadius: "0%",
              }}
            />
          ) : (
            <Avatar
              src="/img/DefaultPackage.png"
              alt="Package Icon"
              sx={{
                width: 48,
                height: 48,
                mr: 2,
                borderRadius: "0%",
              }}
            />
          )}

          <Box display="flex" flexDirection="column" gap={1}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
              <Autocomplete
                freeSolo
                disabled={readOnlyViewer}
                options={[]}
                value={selectedPackage?.rule?.name || ""}
                onInputChange={handlePackageNameChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    error={subjectMissingFromLibrary}
                    helperText={subjectMissingFromLibrary ? "No package matches this subject" : ""}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#1b1a19",
                        color: "#fff",
                        width: "auto",
                        minWidth: "280px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: subjectMissingFromLibrary ? "#d5878b" : "#444",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "10px 14px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: subjectMissingFromLibrary ? "#d5878b" : "#888",
                      },
                      "& .MuiFormHelperText-root": {
                        color: "#d5878b",
                        marginLeft: 0,
                      },
                    }}
                  />
                )}
                sx={{
                  width: "400px",
                }}
              />
            </Box>

            <Autocomplete<PackageOption | string, false, false, true>
              freeSolo
              options={dropdownOptions}
              value={selectedPackage?.activePackageName || ""}
              open={isPackageDropdownOpen}
              onOpen={() => setIsPackageDropdownOpen(true)}
              onClose={() => setIsPackageDropdownOpen(false)}
              disabled={readOnlyViewer}
              getOptionLabel={(option) => typeof option === "string" ? option : option.value}
              isOptionEqualToValue={(option, value) => {
                const optVal = typeof option === "string" ? option : option.value;
                const valVal = typeof value === "string" ? value : value.value;
                return optVal === valVal;
              }}
              onChange={(_, newValue) => {
                const newName = typeof newValue === "string" ? newValue : (newValue?.value || "");
                dispatch(policyEditorActions.updateSelectedPackageActiveName(newName));
              }}
              onInputChange={(_, newValue) => {
                dispatch(policyEditorActions.updateSelectedPackageActiveName(newValue));
              }}
              renderOption={(props, option) => {
                if (typeof option === "string") return <li {...props}>{option}</li>;
                return (
                  <li {...props} key={`${option.source}-${option.value}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <span>{option.value}</span>
                    {option.source === "pending" ? (
                      <Box component="span" sx={{
                        fontSize: "0.65rem", px: 0.75, py: 0.15, borderRadius: "10px",
                        backgroundColor: "rgba(251, 192, 45, 0.18)", color: "#fbc02d",
                      }}>Pending</Box>
                    ) : option.source === "removing" ? (
                      <Box component="span" sx={{
                        fontSize: "0.65rem", px: 0.75, py: 0.15, borderRadius: "10px",
                        backgroundColor: "rgba(213, 135, 139, 0.18)", color: "#d5878b",
                      }}>Removing</Box>
                    ) : option.source === "installed" ? (
                      <Box component="span" sx={{
                        fontSize: "0.65rem", px: 0.75, py: 0.15, borderRadius: "10px",
                        backgroundColor: "rgba(0, 120, 212, 0.15)", color: "#4daafc",
                      }}>Approved</Box>
                    ) : (
                      <Box component="span" sx={{
                        fontSize: "0.65rem", px: 0.75, py: 0.15, borderRadius: "10px",
                        border: "1px solid #605e5c", color: "#a19f9d",
                      }}>Catalog</Box>
                    )}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  label="Applicable packages"
                  {...params}
                  variant="outlined"
                  size="small"
                  error={noApplicablePackage}
                  helperText={noApplicablePackage ? "Pick an applicable package" : ""}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#1b1a19",
                      color: "#fff",
                      width: "auto",
                      minWidth: "200px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: noApplicablePackage ? "#d5878b" : "#444",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "10px 14px",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: noApplicablePackage ? "#d5878b" : "#888",
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#d5878b",
                      marginLeft: 0,
                    },
                  }}
                />
              )}
              sx={{
                width: "400px",
                "& .MuiAutocomplete-popupIndicator": {
                  color: "#fff",
                },
              }}
            />

            <Typography variant="body2" sx={{ color: "#aaa" }}>
              {selectedPackage?.description || ""}
            </Typography>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            borderBottom: "1px solid #333",
            "& .MuiTab-root": { color: "#fff", textTransform: "none", fontWeight: "bold" },
            "& .MuiTabs-flexContainer": {
              flexWrap: "wrap",
            },
          }}
        >
          <Tab label="Applicability" tabIndex={-1} />
          <Tab label="Configuration" tabIndex={-1} />
          {inheritedConfigurationsTabs.map((config, index) => (
            <Tab
              key={config.id}
              label={
                config.isDeleted
                  ? `Inherited Configuration #${index + 1} (Marked for Deletion)`
                  : `Inherited Configuration #${index + 1}`
              }
              sx={config.isDeleted ? { textDecoration: "line-through", opacity: 0.6 } : undefined}
              tabIndex={-1}
            />
          ))}
        </Tabs>

        {selectedPackage && (
          <Box mt={3}>
            {currentTab === 0 ? (
              <ApplicabilityRuleTab readOnlyViewer={readOnlyViewer} />
            ) : currentTab === 1 ? (
              <ConfigurationTab
                configurations={localConfigurations}
                onInsert={handleInsertConfiguredItem}
                onUpdate={handleUpdateConfiguredItem}
                onDelete={handleDeleteConfiguredItem}
                onUndoDelete={handleUndoDeleteConfiguredItem}
                onReorder={handleReorderConfiguredItems}
                notConfiguredRules={notConfiguredRules}
                readOnlyViewer={readOnlyViewer}
              />
            ) : (
              inheritedConfigurationsTabs.map((config, index) =>
                currentTab - 2 === index ? (
                  <InheritedConfigurationTab
                    key={config.id}
                    id={config.id}
                    regex={config.regex}
                    matchingPackages={config.matchingPackages}
                    configurations={config.configurations}
                    notConfiguredRules={config.notConfiguredRules}
                    readOnlyViewer={readOnlyViewer}
                    isDeleted={config.isDeleted}
                  />
                ) : null
              )
            )}
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            backgroundColor: "#1b1a19",
            display: "flex",
            alignItems: "center",
            padding: "10px 35px 10px 86px",
            borderTop: "1px solid #333",
            zIndex: 10,
          }}
        >
          <Box display="flex" gap={1}>
            <Button variant="outlined" color="secondary" onClick={handleCancel}>
              {readOnlyViewer ? "Close" : "Cancel"}
            </Button>
            <Button variant="outlined" color="inherit" disabled={currentTab === 0} onClick={handlePrevious}>
              Previous
            </Button>
            <Button variant="outlined" color="inherit" disabled={currentTab === 1} onClick={handleNext}>
              Next
            </Button>
            <Button variant="contained" color="primary" hidden={readOnlyViewer} onClick={handleSave} disabled={!selectedPackage?.isValid || subjectMissingFromLibrary || noApplicablePackage}>
              Review + Save
            </Button>
          </Box>
        </Box>
      </Box>
      <ErrorMessage
        isVisible={saveErrorMessage !== null}
        onConfirm={() => {
          setSaveErrorMessage(null);
        }}
        title={`Error while saving the rule!`}
        message={saveErrorMessage || ""}
      />
    </ThemeProvider>
  );
};

export default PolicyEditorSubjectView;
